/* iceberg-ui */
import { Flex } from '@auviknetworks/iceberg-ui';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/*  Components  */
import {
    AppNameLinkCell,
    ApplicationsBubblesCell,
    DateFormatCell,
    RiskCell,
    SSOSupportIconsCell,
    StringCell,
    UserBubblesCell,
    UserIconAndNameCell
} from '../../Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from '../../Table';
import { type TablePanel as TablePanelType } from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

/**
 * Cell type definitions for different column types in the table
 * @property {function} application - Renders application name with optional category and link
 * @property {function} date - Renders formatted date values
 * @property {function} risk - Renders risk values
 * @property {function} text - Renders plain text values
 * @property {function} user - Renders user information with icon and name
 * @property {function} users - Renders multiple users as bubbles
 */
const cells: Record<
    | 'application'
    | 'applications'
    | 'date'
    | 'risk'
    | 'ssoCoverage'
    | 'text'
    | 'user'
    | 'users',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
> = {
    application: (props: React.ComponentProps<typeof AppNameLinkCell>) => (
        <AppNameLinkCell {...props} withCategory={false} nameHasLink />
    ),
    applications: ApplicationsBubblesCell,
    date: DateFormatCell,
    risk: RiskCell,
    ssoCoverage: SSOSupportIconsCell,
    text: StringCell,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    user: (props: any) => {
        const userData = props.cell.getValue();
        return <UserIconAndNameCell row={{ original: userData }} />;
    },
    users: UserBubblesCell
};

const TableContent = () => {
    const { t } = useTranslation();
    const {
        queryResults: { data, isFetching },
        schema: { format = {} }
    } = usePanelContext<TablePanelType>();

    const {
        columns = [],
        enableFiltering = false,
        enablePagination = true,
        enableSorting = true,
        pageSize = 5
    } = format;

    const rows = Array.isArray(data) ? data : data?.data;

    const tableColumns = columns.map((column) => ({
        accessorKey: column.accessor,
        cell: cells[column.type as keyof typeof cells] || StringCell,
        header: Header,
        id: column.id || column.accessor,
        maxSize: column.maxSize || null,
        name: t(column.header || ''),
        size: column.size || null
    }));

    return (
        <ManagedTableV2ContextProvider
            columns={tableColumns}
            ignoreSearchParams
            mode="client"
            rows={rows}
            rowsPerPage={pageSize}
            externalLoading={isFetching}
            enableColumnFilters={enableFiltering}
            enableSorting={enableSorting}
        >
            <Flex
                sx={{
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 300px)',
                    minHeight: '400px',
                    overflow: 'auto'
                }}
            >
                <ManagedTableV2UI renderFooter={enablePagination} />
            </Flex>
        </ManagedTableV2ContextProvider>
    );
};

/**
 * Renders a Table panel
 * @param panel - The panel configuration containing data and schema
 * @returns The rendered table panel
 */
export const TablePanel = ({ panel }: { panel: TablePanelType }) => (
    <Panel panel={panel}>
        <TableContent />
    </Panel>
);
